import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL } from '../config';

// Import translations
import pl from '../locales/pl.json';
import en from '../locales/en.json';
import de from '../locales/de.json';
import es from '../locales/es.json';
import it from '../locales/it.json';

const translations = {
  pl,
  en,
  de,
  es,
  it
};

export type Language = 'pl' | 'en' | 'de' | 'es' | 'it';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  syncLanguageFromDatabase: () => Promise<void>;
  t: (key: string, params?: Record<string, any>) => string;
  availableLanguages: { code: Language; name: string; flag: string }[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const availableLanguages = [
  { code: 'pl' as Language, name: 'Polski', flag: '🇵🇱' },
  { code: 'en' as Language, name: 'English', flag: '🇺🇸' },
  { code: 'de' as Language, name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es' as Language, name: 'Español', flag: '🇪🇸' },
  { code: 'it' as Language, name: 'Italiano', flag: '🇮🇹' }
];

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && availableLanguages.some(lang => lang.code === savedLanguage)) {
      return savedLanguage;
    } else {
      localStorage.setItem('language', 'en');
      return 'en';
    }
  });

  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    
    // Update language in database if user is logged in
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/language`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ language: lang })
      });
      
      if (!response.ok) {
        console.error('Failed to update language in database');
      }
    } catch (error) {
      console.error('Error updating language in database:', error);
    }
  };

  const syncLanguageFromDatabase = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        if (userData.language && availableLanguages.some(lang => lang.code === userData.language)) {
          setLanguageState(userData.language as Language);
          localStorage.setItem('language', userData.language);
        }
      }
    } catch (error) {
      console.error('Error syncing language from database:', error);
    }
  };

  const t = (key: string, params?: Record<string, any>): string => {
    const keys = key.split('.');
    let value: any = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to Polish if translation not found
        value = keys.reduce((obj: any, k) => obj?.[k], translations.pl) || key;
        break;
      }
    }
    
    let result = typeof value === 'string' ? value : key;
    
    // Replace parameters if provided
    if (params && typeof result === 'string') {
      Object.keys(params).forEach(paramKey => {
        const regex = new RegExp(`{{${paramKey}}}`, 'g');
        result = result.replace(regex, String(params[paramKey]));
      });
    }
    
    return result;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, syncLanguageFromDatabase, t, availableLanguages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 